import { either } from 'fp-ts/Either'
import * as ioTs from 'io-ts'

// General

const ISO8601DateTime = new ioTs.Type<Date, string, unknown>(
  'DateFromString',
  (input): input is Date => input instanceof Date,
  (input, context) =>
    either.chain(ioTs.string.validate(input, context), (s) => {
      const date = new Date(s)
      return isNaN(date.getTime())
        ? ioTs.failure(input, context)
        : ioTs.success(date)
    }),
  (a) => a.toISOString()
)

function PaginatedObjectResult<P extends ioTs.Props>(type: ioTs.TypeC<P>) {
  return ioTs.type({
    pages: ioTs.union([ioTs.number, ioTs.null]),
    pagination_seed: ioTs.number,
    data: type,
  })
}

export enum UserStatusLevel {
  Regular = 'regular',
  Verified = 'verified',
  Spotlight = 'spotlight',
  Editor = 'editor',
}
const UserStatusLevelType = ioTs.union([
  ioTs.literal(UserStatusLevel.Regular),
  ioTs.literal(UserStatusLevel.Verified),
  ioTs.literal(UserStatusLevel.Spotlight),
  ioTs.literal(UserStatusLevel.Editor),
])

const publicUserFields = {
  id: ioTs.number,
  username: ioTs.union([ioTs.string, ioTs.null]),
  statusLevel: ioTs.union([UserStatusLevelType, ioTs.undefined]),
  isCreator: ioTs.boolean,
  isFeatured: ioTs.boolean,
  isAssisted: ioTs.boolean,
  isVerified: ioTs.boolean,
  isStealth: ioTs.boolean,
  showMerch: ioTs.boolean,
  bio: ioTs.union([ioTs.string, ioTs.null]),
  defaultToLinksPage: ioTs.boolean,
  defaultToFullScreen: ioTs.boolean,
  homepage: ioTs.union([ioTs.string, ioTs.null]),
  instagramUrl: ioTs.union([ioTs.string, ioTs.null]),
  pinterestUrl: ioTs.union([ioTs.string, ioTs.null]),
  amazonUrl: ioTs.union([ioTs.string, ioTs.null]),
  tiktokUrl: ioTs.union([ioTs.string, ioTs.null]),
  spotifyUrl: ioTs.union([ioTs.string, ioTs.null]),
  goodreadsUrl: ioTs.union([ioTs.string, ioTs.null]),
  snapchatUrl: ioTs.union([ioTs.string, ioTs.null]),
  etsyUrl: ioTs.union([ioTs.string, ioTs.null]),
  youtubeUrl: ioTs.union([ioTs.string, ioTs.null]),
  twitterUrl: ioTs.union([ioTs.string, ioTs.null]),
  publicEmail: ioTs.union([ioTs.string, ioTs.null]),
  facebookUrl: ioTs.union([ioTs.string, ioTs.null]),
  applePodcastsUrl: ioTs.union([ioTs.string, ioTs.null]),
  collegeId: ioTs.union([ioTs.number, ioTs.null, ioTs.undefined]),
}

export type CmsUser = ioTs.TypeOf<typeof CmsUserCoder>

const ImageUrlCoder = ioTs.type({
  lowres: ioTs.string,
  full: ioTs.string,
  thumbnail: ioTs.string,
})

export type ImageUrl = ioTs.TypeOf<typeof ImageUrlCoder>

export const CollegeCoder = ioTs.type({
  id: ioTs.number,
  name: ioTs.string,
  shortname: ioTs.string,
  image: ImageUrlCoder,
})
export type College = ioTs.TypeOf<typeof CollegeCoder>

export const CurrentUserCoder = ioTs.type({
  ...publicUserFields,
  isExcludedFromCreatorFund: ioTs.boolean,
  isSuperAdmin: ioTs.boolean,
  isAdmin: ioTs.boolean,
  hasTikTokConnection: ioTs.boolean,
  hasInstagramConnection: ioTs.boolean,
  shouldScrapeInstagram: ioTs.boolean,
  onboardingStartedAt: ioTs.union([ISO8601DateTime, ioTs.null]),
})

export type CurrentUser = ioTs.TypeOf<typeof CurrentUserCoder>

// CMS

export const ConfigCoder = ioTs.type({
  forbiddenProductUrlDomains: ioTs.array(ioTs.string),
})

export type Config = ioTs.TypeOf<typeof ConfigCoder>

export const AffiliateDomainCoder = ioTs.type({
  id: ioTs.number,
  domain: ioTs.string,
  rate: ioTs.number,
  createdAt: ISO8601DateTime,
  updatedAt: ISO8601DateTime,
})

export type AffiliateDomain = ioTs.TypeOf<typeof AffiliateDomainCoder>

export const AffiliateDomainInputCoder = ioTs.type({
  domain: ioTs.string,
  rate: ioTs.number,
})

export type AffiliateDomainInput = ioTs.TypeOf<typeof AffiliateDomainInputCoder>

export const BrandCoder = ioTs.type({
  id: ioTs.number,
  name: ioTs.string,
  domain: ioTs.union([ioTs.string, ioTs.null, ioTs.undefined]),
  createdAt: ISO8601DateTime,
  updatedAt: ISO8601DateTime,
})

export type Brand = ioTs.TypeOf<typeof BrandCoder>

export const BrandInputCoder = ioTs.type({
  name: ioTs.string,
  domain: ioTs.union([ioTs.string, ioTs.null, ioTs.undefined]),
})

export type BrandInput = ioTs.TypeOf<typeof BrandInputCoder>

const categoryFields = {
  id: ioTs.number,
  name: ioTs.string,
  imageId: ioTs.number,
  position: ioTs.union([ioTs.number, ioTs.null]),
  createdAt: ISO8601DateTime,
  updatedAt: ISO8601DateTime,
}

export const CategoryCoder = ioTs.type(categoryFields)

export type Category = ioTs.TypeOf<typeof CategoryCoder>

export const CategoryWithUserIdsCoder = ioTs.type({
  ...categoryFields,
  userIds: ioTs.array(ioTs.number),
})

export type CategoryWithUserIds = ioTs.TypeOf<typeof CategoryWithUserIdsCoder>

export const CategoryInputCoder = ioTs.type({
  name: ioTs.string,
  imageId: ioTs.number,
  position: ioTs.union([ioTs.number, ioTs.null]),
})

export type CategoryInput = ioTs.TypeOf<typeof CategoryInputCoder>

export const UserCoder = ioTs.type(publicUserFields)

export const OmniCsrfCoder = ioTs.type({
  token: ioTs.string,
})
export type OmniCsrf = ioTs.TypeOf<typeof OmniCsrfCoder>

export type User = ioTs.TypeOf<typeof UserCoder>

export const CmsUserCoder = ioTs.type({
  ...publicUserFields,
  email: ioTs.string,
  phoneNumber: ioTs.union([ioTs.string, ioTs.null]),
  isCreator: ioTs.boolean,
  isExcludedFromCreatorFund: ioTs.boolean,
  isDefaultFollow: ioTs.boolean,
  isAdmin: ioTs.boolean,
  isSuperAdmin: ioTs.boolean,
  shouldScrapeInstagram: ioTs.boolean,
  noSms: ioTs.boolean,
  brandId: ioTs.union([ioTs.number, ioTs.null]),
  imageId: ioTs.union([ioTs.number, ioTs.null]),
  backgroundImageId: ioTs.union([ioTs.number, ioTs.null]),
  categoryIds: ioTs.array(ioTs.number),
  follows: ioTs.union([ioTs.number, ioTs.null, ioTs.undefined]),
  saves: ioTs.union([ioTs.number, ioTs.null, ioTs.undefined]),
  mediaViews: ioTs.union([ioTs.number, ioTs.null, ioTs.undefined]),
  lastPublishedMediaDate: ioTs.union([
    ioTs.undefined,
    ioTs.null,
    ISO8601DateTime,
  ]),
  createdAt: ISO8601DateTime,
  updatedAt: ISO8601DateTime,
})

export const UserInputCoder = ioTs.type({
  username: ioTs.union([ioTs.string, ioTs.null]),
  email: ioTs.string,
  phoneNumber: ioTs.union([ioTs.string, ioTs.null]),
  isCreator: ioTs.boolean,
  isExcludedFromCreatorFund: ioTs.boolean,
  isFeatured: ioTs.boolean,
  isAssisted: ioTs.boolean,
  isVerified: ioTs.boolean,
  isDefaultFollow: ioTs.boolean,
  defaultToFullScreen: ioTs.boolean,
  isAdmin: ioTs.boolean,
  isSuperAdmin: ioTs.boolean,
  isStealth: ioTs.boolean,
  showMerch: ioTs.boolean,
  shouldScrapeInstagram: ioTs.boolean,
  noSms: ioTs.boolean,
  brandId: ioTs.union([ioTs.number, ioTs.null]),
  bio: ioTs.union([ioTs.string, ioTs.null]),
  defaultToLinksPage: ioTs.boolean,
  imageId: ioTs.union([ioTs.number, ioTs.null]),
  backgroundImageId: ioTs.union([ioTs.number, ioTs.null]),
  homepage: ioTs.union([ioTs.string, ioTs.null]),
  instagramUrl: ioTs.union([ioTs.string, ioTs.null]),
  pinterestUrl: ioTs.union([ioTs.string, ioTs.null]),
  amazonUrl: ioTs.union([ioTs.string, ioTs.null]),
  tiktokUrl: ioTs.union([ioTs.string, ioTs.null]),
  spotifyUrl: ioTs.union([ioTs.string, ioTs.null]),
  goodreadsUrl: ioTs.union([ioTs.string, ioTs.null]),
  snapchatUrl: ioTs.union([ioTs.string, ioTs.null]),
  etsyUrl: ioTs.union([ioTs.string, ioTs.null]),
  youtubeUrl: ioTs.union([ioTs.string, ioTs.null]),
  twitterUrl: ioTs.union([ioTs.string, ioTs.null]),
  publicEmail: ioTs.union([ioTs.string, ioTs.null]),
  facebookUrl: ioTs.union([ioTs.string, ioTs.null]),
  applePodcastsUrl: ioTs.union([ioTs.string, ioTs.null]),
  categoryIds: ioTs.array(ioTs.number),
})

export type UserInput = ioTs.TypeOf<typeof UserInputCoder>

const ContentStateProcessingCoder = ioTs.type({
  type: ioTs.literal('processing'),
})

const ImageStateProcessedCoder = ioTs.type({
  type: ioTs.literal('processed'),
  value: ioTs.type({
    full: ioTs.string,
  }),
})

const VideoStateProcessedCoder = ioTs.type({
  type: ioTs.literal('processed'),
  value: ioTs.string,
})

const ContentStateErrorCoder = ioTs.type({
  type: ioTs.literal('error'),
})

const ImageStateCoder = ioTs.union([
  ContentStateProcessingCoder,
  ImageStateProcessedCoder,
  ContentStateErrorCoder,
])

export type ImageState = ioTs.TypeOf<typeof ImageStateCoder>

export const isImageStateProcessing = (
  state: ImageState
): state is ioTs.TypeOf<typeof ContentStateProcessingCoder> =>
  state.type === 'processing'

export const isImageStateProcessed = (
  state: ImageState
): state is ioTs.TypeOf<typeof ImageStateProcessedCoder> =>
  state.type === 'processed'

export const isImageStateError = (
  state: ImageState
): state is ioTs.TypeOf<typeof ContentStateErrorCoder> => state.type === 'error'

const VideoStateCoder = ioTs.union([
  ContentStateProcessingCoder,
  VideoStateProcessedCoder,
  ContentStateErrorCoder,
])

export type VideoState = ioTs.TypeOf<typeof VideoStateCoder>

export const isVideoStateProcessing = (
  state: VideoState
): state is ioTs.TypeOf<typeof ContentStateProcessingCoder> =>
  state.type === 'processing'

export const isVideoStateProcessed = (
  state: VideoState
): state is ioTs.TypeOf<typeof VideoStateProcessedCoder> =>
  state.type === 'processed'

export const isVideoStateError = (
  state: VideoState
): state is ioTs.TypeOf<typeof ContentStateErrorCoder> => state.type === 'error'

export const ImageCoder = ioTs.type({
  id: ioTs.number,
  state: ImageStateCoder,
  createdAt: ISO8601DateTime,
  updatedAt: ISO8601DateTime,
})

export type Image = ioTs.TypeOf<typeof ImageCoder>

const ImageInputS3Coder = ioTs.type({
  type: ioTs.literal('s3'),
  value: ioTs.string,
})

const ImageInputUrlCoder = ioTs.type({
  type: ioTs.literal('url'),
  value: ioTs.string,
})

export const ImageInputCoder = ioTs.union([
  ImageInputS3Coder,
  ImageInputUrlCoder,
])

export type ImageInput = ioTs.TypeOf<typeof ImageInputCoder>

const ImageContentCoder = ioTs.type({
  type: ioTs.literal('image'),
  value: ioTs.number,
})

export type ImageContent = ioTs.TypeOf<typeof ImageContentCoder>

export const VideoCoder = ioTs.type({
  id: ioTs.number,
  state: VideoStateCoder,
  createdAt: ISO8601DateTime,
  updatedAt: ISO8601DateTime,
})

export type Video = ioTs.TypeOf<typeof VideoCoder>

const VideoInputMuxCoder = ioTs.type({
  type: ioTs.literal('mux'),
  value: ioTs.string,
})

const VideoInputUrlCoder = ioTs.type({
  type: ioTs.literal('url'),
  value: ioTs.string,
})

export const VideoInputCoder = ioTs.union([
  VideoInputMuxCoder,
  VideoInputUrlCoder,
])

export type VideoInput = ioTs.TypeOf<typeof VideoInputCoder>

const VideoContentCoder = ioTs.type({
  type: ioTs.literal('video'),
  value: ioTs.number,
})

export type VideoContent = ioTs.TypeOf<typeof VideoContentCoder>

const MediaContentCoder = ioTs.union([ImageContentCoder, VideoContentCoder])

export type MediaContent = ioTs.TypeOf<typeof MediaContentCoder>

export const isImageMediaContent = (
  content: MediaContent
): content is ImageContent => content.type === 'image'

export const isVideoMediaContent = (
  content: MediaContent
): content is VideoContent => content.type === 'video'

const MediaStateCoder = ioTs.union([
  ioTs.literal('adminDraft'),
  ioTs.literal('creatorDraft'),
  ioTs.literal('published'),
])

export type MediaState = ioTs.TypeOf<typeof MediaStateCoder>

export const MediaCoder = ioTs.type({
  id: ioTs.number,
  userId: ioTs.number,
  state: MediaStateCoder,
  caption: ioTs.union([ioTs.string, ioTs.null]),
  content: MediaContentCoder,
  socialMediaPostId: ioTs.union([ioTs.number, ioTs.null]),
  user: UserCoder,
  productIds: ioTs.array(ioTs.number),
  tagIds: ioTs.array(ioTs.number),
  createdAt: ISO8601DateTime,
  updatedAt: ISO8601DateTime,
  position: ioTs.union([ioTs.number, ioTs.null]),
  views: ioTs.number,
  saves: ioTs.number,
  score: ioTs.union([ioTs.number, ioTs.undefined]),
  commentsCount: ioTs.union([ioTs.number, ioTs.undefined]),
})

export type Media = ioTs.TypeOf<typeof MediaCoder>

export const MediaInputCoder = ioTs.type({
  userId: ioTs.number,
  state: MediaStateCoder,
  caption: ioTs.union([ioTs.string, ioTs.null]),
  content: MediaContentCoder,
  socialMediaPostId: ioTs.union([ioTs.number, ioTs.null]),
  productIds: ioTs.array(ioTs.number),
  tagIds: ioTs.array(ioTs.number),
  position: ioTs.union([ioTs.number, ioTs.null]),
})

export type MediaInput = ioTs.TypeOf<typeof MediaInputCoder>

const brandFeatureFields = {
  id: ioTs.number,
  brandId: ioTs.number,
  imageId: ioTs.number,
  createdAt: ISO8601DateTime,
  updatedAt: ISO8601DateTime,
}

export const BrandFeatureCoder = ioTs.type(brandFeatureFields)

export type BrandFeature = ioTs.TypeOf<typeof BrandFeatureCoder>

export const BrandFeatureWithUsernameAndImageCoder = ioTs.type({
  ...brandFeatureFields,
  username: ioTs.union([ioTs.string, ioTs.null]),
  image: ImageUrlCoder,
})

export type BrandFeatureWithUsernameAndImage = ioTs.TypeOf<
  typeof BrandFeatureWithUsernameAndImageCoder
>

export const BrandFeatureInputCoder = ioTs.type({
  brandId: ioTs.number,
  imageId: ioTs.number,
})

export type BrandFeatureInput = ioTs.TypeOf<typeof BrandFeatureInputCoder>

const creatorFeatureFields = {
  id: ioTs.number,
  userId: ioTs.number,
  imageId: ioTs.number,
  createdAt: ISO8601DateTime,
  updatedAt: ISO8601DateTime,
}

export const CreatorFeatureCoder = ioTs.type(creatorFeatureFields)

export type CreatorFeature = ioTs.TypeOf<typeof CreatorFeatureCoder>

export const CreatorFeatureWithUsernameAndImageCoder = ioTs.type({
  ...creatorFeatureFields,
  username: ioTs.union([ioTs.string, ioTs.null]),
  image: ImageUrlCoder,
})

export type CreatorFeatureWithUsernameAndImage = ioTs.TypeOf<
  typeof CreatorFeatureWithUsernameAndImageCoder
>

export const CreatorFeatureInputCoder = ioTs.type({
  userId: ioTs.number,
  imageId: ioTs.number,
})

export type CreatorFeatureInput = ioTs.TypeOf<typeof CreatorFeatureInputCoder>

const productFeatureFields = {
  id: ioTs.number,
  productId: ioTs.number,
  createdAt: ISO8601DateTime,
  updatedAt: ISO8601DateTime,
}

export const ProductFeatureCoder = ioTs.type(productFeatureFields)

export type ProductFeature = ioTs.TypeOf<typeof ProductFeatureCoder>

export const ProductFeatureInputCoder = ioTs.type({
  productId: ioTs.number,
})

export type ProductFeatureInput = ioTs.TypeOf<typeof ProductFeatureInputCoder>

export const CreatorLaunchImageCoder = ioTs.type({
  id: ioTs.number,
  imageId: ioTs.number,
  createdAt: ISO8601DateTime,
  updatedAt: ISO8601DateTime,
})

export type CreatorLaunchImage = ioTs.TypeOf<typeof CreatorLaunchImageCoder>

export const CreatorLaunchImageInputCoder = ioTs.type({
  imageId: ioTs.number,
})

export type CreatorLaunchImageInput = ioTs.TypeOf<
  typeof CreatorLaunchImageInputCoder
>

export const CreatorLaunchImageWithImageCoder = ioTs.type({
  id: ioTs.number,
  imageId: ioTs.number,
  image: ImageUrlCoder,
  createdAt: ISO8601DateTime,
  updatedAt: ISO8601DateTime,
})

export type CreatorLaunchImageWithImage = ioTs.TypeOf<
  typeof CreatorLaunchImageWithImageCoder
>

const productFields = {
  id: ioTs.number,
  brandId: ioTs.number,
  brandName: ioTs.string,
  category: ioTs.union([ioTs.string, ioTs.null]),
  name: ioTs.string,
  url: ioTs.string,
  originalUrl: ioTs.union([ioTs.string, ioTs.null]),
  resolvedUrl: ioTs.union([ioTs.string, ioTs.null]),
  imageId: ioTs.number,
  price: ioTs.union([ioTs.number, ioTs.null]),
  saves: ioTs.number,
  views: ioTs.union([ioTs.number, ioTs.null, ioTs.undefined]),
  outgoingClicks: ioTs.union([ioTs.number, ioTs.null, ioTs.undefined]),
  createdAt: ISO8601DateTime,
  updatedAt: ISO8601DateTime,
}

export const ProductCoder = ioTs.type(productFields)

export type Product = ioTs.TypeOf<typeof ProductCoder>

export const ProductWithImageAndRelatedProductIdsCoder = ioTs.type({
  ...productFields,
  views: ioTs.number,
  image: ImageUrlCoder,
  relatedProductIds: ioTs.array(ioTs.number),
})

export type ProductWithImageAndRelatedProductIds = ioTs.TypeOf<
  typeof ProductWithImageAndRelatedProductIdsCoder
>

export const ProductInputCoder = ioTs.type({
  productUrlId: ioTs.union([ioTs.number, ioTs.null]),
  brandId: ioTs.number,
  category: ioTs.union([ioTs.string, ioTs.null]),
  name: ioTs.string,
  url: ioTs.string,
  imageId: ioTs.number,
  price: ioTs.union([ioTs.number, ioTs.null]),
  relatedProductIds: ioTs.array(ioTs.number),
})

export type ProductInput = ioTs.TypeOf<typeof ProductInputCoder>

export const ProductSuggestionCoder = ioTs.type({
  brand: ioTs.union([BrandCoder, ioTs.null]),
  name: ioTs.string,
  image: ioTs.union([ioTs.string, ioTs.null]),
  url: ioTs.union([ioTs.string, ioTs.null]),
  price: ioTs.union([ioTs.number, ioTs.null]),
})

export type ProductSuggestion = ioTs.TypeOf<typeof ProductSuggestionCoder>

export const ProductSuggestionResponseCoder = ioTs.union([
  ProductSuggestionCoder,
  ioTs.null,
])

export type ProductSuggestionResponse = ioTs.TypeOf<
  typeof ProductSuggestionResponseCoder
>

export const ImagePresignedUrlCoder = ioTs.type({
  presignedUrl: ioTs.string,
  key: ioTs.string,
})

export type ImagePresignedUrl = ioTs.TypeOf<typeof ImagePresignedUrlCoder>

export const ImagePresignedUrlInputCoder = ioTs.type({
  filename: ioTs.string,
})

export type ImagePresignedUrlInput = ioTs.TypeOf<
  typeof ImagePresignedUrlInputCoder
>

export const VideoPresignedUrlCoder = ioTs.type({
  presignedUrl: ioTs.string,
  id: ioTs.string,
})

export type VideoPresignedUrl = ioTs.TypeOf<typeof VideoPresignedUrlCoder>

export const TagCoder = ioTs.type({
  id: ioTs.number,
  name: ioTs.string,
  position: ioTs.union([ioTs.number, ioTs.null]),
  notificationMessage: ioTs.union([ioTs.string, ioTs.null, ioTs.undefined]),
  includeFeaturesInFeed: ioTs.boolean,
  usedForCreatorFund: ioTs.boolean,
  createdAt: ISO8601DateTime,
  updatedAt: ISO8601DateTime,
})

export type Tag = ioTs.TypeOf<typeof TagCoder>

export const TagInputCoder = ioTs.type({
  name: ioTs.string,
  position: ioTs.union([ioTs.number, ioTs.null]),
  notificationMessage: ioTs.union([ioTs.string, ioTs.null, ioTs.undefined]),
  includeFeaturesInFeed: ioTs.boolean,
})

export type TagInput = ioTs.TypeOf<typeof TagInputCoder>

export const LabelCoder = ioTs.type({
  id: ioTs.number,
  name: ioTs.string,
  useForProductRecommendations: ioTs.boolean,
  createdAt: ISO8601DateTime,
  updatedAt: ISO8601DateTime,
})

export type Label = ioTs.TypeOf<typeof LabelCoder>

export const LabelInputCoder = ioTs.type({
  name: ioTs.string,
  useForProductRecommendations: ioTs.boolean,
})

export type LabelInput = ioTs.TypeOf<typeof LabelInputCoder>

// Consumer

export const BookmarkCoder = ioTs.type({
  id: ioTs.number,
  mediaId: ioTs.union([ioTs.number, ioTs.null, ioTs.undefined]),
  productId: ioTs.number,
})

export type Bookmark = ioTs.TypeOf<typeof BookmarkCoder>

export const BookmarkInputCoder = ioTs.type({
  mediaId: ioTs.union([ioTs.number, ioTs.null, ioTs.undefined]),
  userListId: ioTs.union([ioTs.number, ioTs.null, ioTs.undefined]),
  productId: ioTs.number,
})

export type BookmarkInput = ioTs.TypeOf<typeof BookmarkInputCoder>

const FeedImageContentCoder = ioTs.type({
  type: ioTs.literal('image'),
  value: ImageUrlCoder,
})

export type FeedImageContent = ioTs.TypeOf<typeof FeedImageContentCoder>

const FeedVideoContentCoder = ioTs.type({
  type: ioTs.literal('video'),
  value: ioTs.string,
})

export type FeedVideoContent = ioTs.TypeOf<typeof FeedVideoContentCoder>

const FeedContentCoder = ioTs.union([
  FeedImageContentCoder,
  FeedVideoContentCoder,
])

export type FeedContent = ioTs.TypeOf<typeof FeedContentCoder>

export const isImageFeedContent = (
  content: FeedContent
): content is FeedImageContent => content.type === 'image'

export const isVideoFeedContent = (
  content: FeedContent
): content is FeedVideoContent => content.type === 'video'

export const videoThumbnail = (video: FeedVideoContent): string =>
  `https://image.mux.com/${video.value}/thumbnail.jpg`

export const ProductWithImageCoder = ioTs.type({
  ...productFields,
  image: ImageUrlCoder,
  matchesSearch: ioTs.union([ioTs.boolean, ioTs.undefined]),
})

export type ProductWithImage = ioTs.TypeOf<typeof ProductWithImageCoder>

export const ProductWithImageAndMadatoryCoder = ioTs.type({
  ...productFields,
  image: ImageUrlCoder,
  mandatory: ioTs.union([ioTs.boolean, ioTs.undefined]),
  matchesSearch: ioTs.union([ioTs.boolean, ioTs.undefined]),
})

export type ProductWithImageAndMadatory = ioTs.TypeOf<
  typeof ProductWithImageAndMadatoryCoder
>

export const UserWithImagesCoder = ioTs.type({
  ...publicUserFields,
  image: ioTs.union([ImageUrlCoder, ioTs.null]),
  backgroundImage: ioTs.union([ImageUrlCoder, ioTs.null]),
})

export type UserWithImages = ioTs.TypeOf<typeof UserWithImagesCoder>

export const UsersWithImagesCoder = ioTs.array(UserWithImagesCoder)

export const UsernameValidationCoder = ioTs.type({
  valid: ioTs.boolean,
  unique: ioTs.boolean,
  reserved: ioTs.boolean,
})

export type UsernameValidation = ioTs.TypeOf<typeof UsernameValidationCoder>

export type UsersWithImages = ioTs.TypeOf<typeof UsersWithImagesCoder>

export const CustomLinkCoder = ioTs.type({
  id: ioTs.number,
  userId: ioTs.number,
  name: ioTs.string,
  url: ioTs.string,
  position: ioTs.number,
  imageId: ioTs.union([ioTs.number, ioTs.null, ioTs.undefined]),
  image: ioTs.union([ImageUrlCoder, ioTs.null, ioTs.undefined]),
})

export type CustomLink = ioTs.TypeOf<typeof CustomLinkCoder>

export const CustomLinkInputCoder = ioTs.type({
  userId: ioTs.number,
  name: ioTs.string,
  url: ioTs.string,
  position: ioTs.number,
  imageId: ioTs.union([ioTs.number, ioTs.null, ioTs.undefined]),
})

export type CustomLinkInput = ioTs.TypeOf<typeof CustomLinkInputCoder>

export const UserStatsCoder = ioTs.type({
  follows: ioTs.number,
  saves: ioTs.number,
  media: ioTs.number,
  mediaViews: ioTs.number,
  brandMediaViews: ioTs.union([ioTs.number, ioTs.null]),
  behindTheGateViews: ioTs.number,
})
export type UserStats = ioTs.TypeOf<typeof UserStatsCoder>

export const UserProfileCoder = ioTs.type({
  ...publicUserFields,
  image: ioTs.union([ImageUrlCoder, ioTs.null]),
  backgroundImage: ioTs.union([ImageUrlCoder, ioTs.null]),
  showEdits: ioTs.boolean,
  customLinks: ioTs.array(CustomLinkCoder),
  stats: ioTs.union([UserStatsCoder, ioTs.null]),
})

export type UserProfile = ioTs.TypeOf<typeof UserProfileCoder>

export const FeedMediaCoder = ioTs.type({
  type: ioTs.literal('media'),
  id: ioTs.number,
  user: UserWithImagesCoder,
  state: MediaStateCoder,
  caption: ioTs.union([ioTs.string, ioTs.null]),
  content: FeedContentCoder,
  coverImage: ioTs.union([ImageUrlCoder, ioTs.null]),
  thumbnail: ioTs.union([ioTs.string, ioTs.null]),
  products: ioTs.array(ProductWithImageCoder),
  numViews: ioTs.number,
  commentsCount: ioTs.union([ioTs.number, ioTs.undefined]),
  createdAt: ISO8601DateTime,
  updatedAt: ISO8601DateTime,
})

const editFields = {
  id: ioTs.number,
  name: ioTs.string,
  uses: ioTs.union([ioTs.number, ioTs.undefined]),
  views: ioTs.union([ioTs.number, ioTs.undefined]),
  position: ioTs.union([ioTs.number, ioTs.null]),
  isRodeoExclusive: ioTs.boolean,
  coverImageId: ioTs.union([ioTs.number, ioTs.null]),
  createdAt: ISO8601DateTime,
  updatedAt: ISO8601DateTime,
}

export const EditCoder = ioTs.type(editFields)

export type Edit = ioTs.TypeOf<typeof EditCoder>

export const EditInputCoder = ioTs.type({
  name: ioTs.string,
  position: ioTs.union([ioTs.number, ioTs.null]),
  isRodeoExclusive: ioTs.boolean,
  coverImageId: ioTs.union([ioTs.number, ioTs.null]),
})

export type EditInput = ioTs.TypeOf<typeof EditInputCoder>

export const EditPublicCoder = ioTs.type({
  ...editFields,
  coverImage: ioTs.union([ImageUrlCoder, ioTs.null]),
})

export type EditPublic = ioTs.TypeOf<typeof EditPublicCoder>

const userListFields = {
  id: ioTs.number,
  name: ioTs.string,
  userId: ioTs.number,
  editId: ioTs.union([ioTs.number, ioTs.undefined, ioTs.null]),
  isPublic: ioTs.boolean,
  createdAt: ISO8601DateTime,
  updatedAt: ISO8601DateTime,
  products: ioTs.union([
    ioTs.array(ProductWithImageAndMadatoryCoder),
    ioTs.undefined,
  ]),
}

export const UserListCoder = ioTs.type(userListFields)

export type UserList = ioTs.TypeOf<typeof UserListCoder>

export const UserListInputCoder = ioTs.type({
  userId: ioTs.number,
  name: ioTs.string,
  editId: ioTs.union([ioTs.number, ioTs.undefined, ioTs.null]),
  isPublic: ioTs.boolean,
  productIds: ioTs.array(ioTs.number),
})

export type UserListInput = ioTs.TypeOf<typeof UserListInputCoder>

export const UserListProductsInputCoder = ioTs.type({
  productIds: ioTs.array(ioTs.number),
})

export type UserListProductsInput = ioTs.TypeOf<
  typeof UserListProductsInputCoder
>

export const UserListPublicCoder = ioTs.type({
  ...userListFields,
  coverImage: ioTs.union([ImageUrlCoder, ioTs.null]),
  type: ioTs.union([ioTs.literal('edit'), ioTs.undefined]),
  // coverImage: ioTs.union([ImageUrlCoder, ioTs.null]),
  edit: ioTs.union([EditPublicCoder, ioTs.null]),
  products: ioTs.array(ProductWithImageAndMadatoryCoder),
  user: UserWithImagesCoder,
})
export type UserListPublic = ioTs.TypeOf<typeof UserListPublicCoder>

export type FeedMedia = ioTs.TypeOf<typeof FeedMediaCoder>

export const FeedFeaturedBrandCoder = ioTs.type({
  type: ioTs.literal('featuredBrands'),
  brands: ioTs.array(BrandFeatureWithUsernameAndImageCoder),
})

export type FeedFeaturedBrand = ioTs.TypeOf<typeof FeedFeaturedBrandCoder>

export const FeedFeaturedUsersCoder = ioTs.type({
  type: ioTs.literal('featuredUsers'),
  users: ioTs.array(CreatorFeatureWithUsernameAndImageCoder),
})

export type FeedFeaturedUsers = ioTs.TypeOf<typeof FeedFeaturedUsersCoder>

export const FeedFeaturedProductsCoder = ioTs.type({
  type: ioTs.literal('featuredProducts'),
  products: ioTs.array(ProductWithImageCoder),
})

export type FeedFeaturedProducts = ioTs.TypeOf<typeof FeedFeaturedProductsCoder>

export const FeedItemCoder = ioTs.union([
  FeedMediaCoder,
  FeedFeaturedUsersCoder,
  FeedFeaturedProductsCoder,
  FeedFeaturedBrandCoder,
  UserListPublicCoder,
])

export type FeedItem = ioTs.TypeOf<typeof FeedItemCoder>

export const isFeedItemMedia = (item: FeedItem): item is FeedMedia =>
  item.type === 'media'

export const isFeedItemFeaturedUsers = (
  item: FeedItem
): item is FeedFeaturedUsers => item.type === 'featuredUsers'

export const isFeedItemFeaturedProducts = (
  item: FeedItem
): item is FeedFeaturedProducts => item.type === 'featuredProducts'

export const isFeedItemFeaturedBrands = (
  item: FeedItem
): item is FeedFeaturedBrand => item.type === 'featuredBrands'

export const isFeedItemEdit = (item: FeedItem): item is UserListPublic =>
  item.type === 'edit'

export const UserFeedCoder = ioTs.type({
  user: UserProfileCoder,
  media: ioTs.array(FeedItemCoder),
})

export type UserFeed = ioTs.TypeOf<typeof UserFeedCoder>

export const CategoryFeedCoder = ioTs.type({
  category: CategoryCoder,
  media: ioTs.array(FeedItemCoder),
})

export type CategoryFeed = ioTs.TypeOf<typeof CategoryFeedCoder>

export const BrandFeedCoder = ioTs.type({
  brand: BrandCoder,
  media: ioTs.array(FeedItemCoder),
})

export type BrandFeed = ioTs.TypeOf<typeof BrandFeedCoder>

export const FeaturedUsersFeedCoder = ioTs.type({
  media: ioTs.array(FeedItemCoder),
})

export type FeaturedUsersFeed = ioTs.TypeOf<typeof FeaturedUsersFeedCoder>

export const TagFeedCoder = ioTs.type({
  tag: TagCoder,
  media: ioTs.array(FeedItemCoder),
})

export type TagFeed = ioTs.TypeOf<typeof TagFeedCoder>

export const PaginatedTagFeedCoder = PaginatedObjectResult(TagFeedCoder)

export type PaginatedTagFeed = ioTs.TypeOf<typeof PaginatedTagFeedCoder>

export const FeedCoder = ioTs.type({
  media: ioTs.array(FeedItemCoder),
})

export type Feed = ioTs.TypeOf<typeof FeedCoder>

export const PaginatedFeedCoder = PaginatedObjectResult(FeedCoder)

export type PaginatedFeed = ioTs.TypeOf<typeof PaginatedFeedCoder>

export const LabelFeedCoder = ioTs.type({
  label: LabelCoder,
  media: ioTs.array(FeedItemCoder),
})

export type LabelFeed = ioTs.TypeOf<typeof LabelFeedCoder>

export const isUserFeed = (
  feed:
    | UserFeed
    | CategoryFeed
    | BrandFeed
    | LabelFeed
    | FeaturedUsersFeed
    | PaginatedFeed
): feed is UserFeed => 'user' in feed

export const isCategoryFeed = (
  feed:
    | UserFeed
    | CategoryFeed
    | BrandFeed
    | LabelFeed
    | FeaturedUsersFeed
    | PaginatedFeed
): feed is CategoryFeed => 'category' in feed

export const isBrandFeed = (
  feed:
    | UserFeed
    | CategoryFeed
    | BrandFeed
    | LabelFeed
    | FeaturedUsersFeed
    | PaginatedFeed
): feed is BrandFeed => 'brand' in feed

export const isPaginatedFeed = (
  feed:
    | UserFeed
    | CategoryFeed
    | BrandFeed
    | LabelFeed
    | FeaturedUsersFeed
    | PaginatedFeed
): feed is PaginatedFeed => 'data' in feed && 'pages' in feed

export const isTagFeed = (
  feed:
    | UserFeed
    | CategoryFeed
    | BrandFeed
    | TagFeed
    | LabelFeed
    | FeaturedUsersFeed
    | PaginatedFeed
): feed is TagFeed => 'tag' in feed && !!feed['tag']

export const isLabelFeed = (
  feed:
    | UserFeed
    | CategoryFeed
    | BrandFeed
    | TagFeed
    | LabelFeed
    | FeaturedUsersFeed
    | PaginatedFeed
): feed is LabelFeed => 'label' in feed

export const ProductDetailsCoder = ioTs.type({
  product: ProductWithImageCoder,
  relatedProducts: ioTs.array(ProductWithImageCoder),
  brandProducts: ioTs.array(ProductWithImageCoder),
})

export type ProductDetails = ioTs.TypeOf<typeof ProductDetailsCoder>

export const ProductUrlCoder = ioTs.type({
  id: ioTs.number,
  url: ioTs.string,
  createdAt: ISO8601DateTime,
  product: ioTs.union([ProductWithImageCoder, ioTs.null]),
})

export type ProductUrl = ioTs.TypeOf<typeof ProductUrlCoder>

export const DetectedProductCoder = ioTs.type({
  product: ProductCoder,
  image: ImageUrlCoder,
})

export type DetectedProduct = ioTs.TypeOf<typeof DetectedProductCoder>

export const CategoryWithImageAndUsersCoder = ioTs.type({
  ...categoryFields,
  image: ImageUrlCoder,
  users: ioTs.array(UserWithImagesCoder),
})

export type CategoryWithImageAndUsers = ioTs.TypeOf<
  typeof CategoryWithImageAndUsersCoder
>

export const FeaturedCategoriesCoder = ioTs.array(
  CategoryWithImageAndUsersCoder
)

export type FeaturedCategories = ioTs.TypeOf<typeof FeaturedCategoriesCoder>

export const BasicMediaCoder = ioTs.type({
  id: ioTs.number,
  user: UserCoder,
  caption: ioTs.union([ioTs.string, ioTs.null]),
  content: FeedContentCoder,
  createdAt: ISO8601DateTime,
  updatedAt: ISO8601DateTime,
})

export type RoundupMedia = ioTs.TypeOf<typeof BasicMediaCoder>

export const RoundupGroupCoder = ioTs.type({
  media: ioTs.union([BasicMediaCoder, ioTs.null]),
  products: ioTs.array(ProductWithImageCoder),
})

export type RoundupGroup = ioTs.TypeOf<typeof RoundupGroupCoder>

export const RoundupCoder = ioTs.type({
  groups: ioTs.array(RoundupGroupCoder),
})

export type Roundup = ioTs.TypeOf<typeof RoundupCoder>

export const VideoPlaybackIdCoder = ioTs.type({
  playbackId: ioTs.string,
})

export type VideoPlaybackId = ioTs.TypeOf<typeof VideoPlaybackIdCoder>

export const MediaQueueItemTypeCoder = ioTs.union([
  ioTs.literal('post'),
  ioTs.literal('story'),
  ioTs.null,
])

export type MediaQueueItemType = ioTs.TypeOf<typeof MediaQueueItemTypeCoder>

export const MediaQueueItemSourceTypeCoder = ioTs.union([
  ioTs.literal('socialMediaPost'),
  ioTs.literal('queuedImage'),
  ioTs.null,
])

export type MediaQueueItemSourceType = ioTs.TypeOf<
  typeof MediaQueueItemSourceTypeCoder
>

export const MediaQueueItemCoder = ioTs.type({
  id: ioTs.number,
  sourceType: MediaQueueItemSourceTypeCoder,
  type: MediaQueueItemTypeCoder,
  caption: ioTs.union([ioTs.string, ioTs.null]),
  contentImageId: ioTs.union([ioTs.number, ioTs.null]),
  contentVideoId: ioTs.union([ioTs.number, ioTs.null]),
  timestamp: ISO8601DateTime,
  contentType: ioTs.union([ioTs.literal('image'), ioTs.literal('video')]),
  thumbnailImage: ImageUrlCoder,
})

export type MediaQueueItem = ioTs.TypeOf<typeof MediaQueueItemCoder>

export const MediaQueueSourceItemsCoder = ioTs.type({
  items: ioTs.array(MediaQueueItemCoder),
})

export type MediaQueueSourceItems = ioTs.TypeOf<
  typeof MediaQueueSourceItemsCoder
>

export const MediaQueueSourceErrorCoder = ioTs.type({
  error: ioTs.union([
    ioTs.literal('loginCanceled'),
    ioTs.literal('mismatchedScopes'),
    ioTs.literal('noPage'),
    ioTs.literal('pageNotLinkedToInstagram'),
    ioTs.literal('providerError'),
    ioTs.literal('generic'),
  ]),
})

export type MediaQueueSourceError = ioTs.TypeOf<
  typeof MediaQueueSourceErrorCoder
>

export type MediaQueueSourceErrorType = ioTs.TypeOf<
  typeof MediaQueueSourceErrorCoder
>['error']

export const MediaQueueSourceCoder = ioTs.union([
  MediaQueueSourceItemsCoder,
  MediaQueueSourceErrorCoder,
])

export type MediaQueueSource = ioTs.TypeOf<typeof MediaQueueSourceCoder>

export const isMediaQueueItems = (
  source: MediaQueueSource
): source is ioTs.TypeOf<typeof MediaQueueSourceItemsCoder> => 'items' in source

export const isMediaQueueError = (
  source: MediaQueueSource
): source is ioTs.TypeOf<typeof MediaQueueSourceErrorCoder> => 'error' in source

export const MediaQueueCoder = ioTs.type({
  images: ioTs.array(MediaQueueItemCoder),
  instagram: MediaQueueSourceCoder,
  tikTok: MediaQueueSourceCoder,
})

export type MediaQueue = ioTs.TypeOf<typeof MediaQueueCoder>

export const OAuthLoginUrlCoder = ioTs.type({
  url: ioTs.string,
})

export type OAuthLoginUrl = ioTs.TypeOf<typeof OAuthLoginUrlCoder>

const SearchResultCreatorCoder = ioTs.type({
  type: ioTs.literal('creator'),
  value: UserWithImagesCoder,
})

const SearchResultBrandCoder = ioTs.type({
  type: ioTs.literal('brand'),
  value: BrandCoder,
})

const SearchResultLabelCoder = ioTs.type({
  type: ioTs.literal('label'),
  value: LabelCoder,
})

export const SearchResultCoder = ioTs.union([
  SearchResultCreatorCoder,
  SearchResultBrandCoder,
  SearchResultLabelCoder,
])

export type SearchResult = ioTs.TypeOf<typeof SearchResultCoder>

export const FollowCoder = ioTs.type({
  id: ioTs.number,
  creatorId: ioTs.number,
})

export type Follow = ioTs.TypeOf<typeof FollowCoder>

export const FollowInputCoder = ioTs.type({
  creatorId: ioTs.number,
})

export type FollowInput = ioTs.TypeOf<typeof FollowInputCoder>

const MessageGroupCoder = ioTs.union([
  ioTs.literal('creators'),
  ioTs.literal('featuredCreators'),
  ioTs.literal('creatorFundCreators'),
])

export type MessageGroup = ioTs.TypeOf<typeof MessageGroupCoder>

const MessageRecipientUserIdCoder = ioTs.type({ userId: ioTs.number })

export type MessageRecipientUserId = ioTs.TypeOf<
  typeof MessageRecipientUserIdCoder
>

const MessageRecipientGroupCoder = ioTs.type({ group: MessageGroupCoder })

export type MessageRecipientGroup = ioTs.TypeOf<
  typeof MessageRecipientGroupCoder
>

const MessageRecipientCoder = ioTs.union([
  MessageRecipientUserIdCoder,
  MessageRecipientGroupCoder,
])

export const isMessageRecipientGroup = (
  recipient: MessageRecipient
): recipient is MessageRecipientGroup => 'group' in recipient

export type MessageRecipient = ioTs.TypeOf<typeof MessageRecipientCoder>

export const MessageContentCoder = ioTs.union([
  ioTs.type({
    text: ioTs.string,
  }),
  ioTs.type({
    taggedMedia: ioTs.type({
      tagId: ioTs.number,
      mediaId: ioTs.number,
    }),
  }),
])

export type MessageContent = ioTs.TypeOf<typeof MessageContentCoder>

export const MessageInputCoder = ioTs.type({
  recipient: MessageRecipientCoder,
  content: MessageContentCoder,
  imageId: ioTs.union([ioTs.number, ioTs.null]),
})

export type MessageInput = ioTs.TypeOf<typeof MessageInputCoder>

export const UserEditCoder = ioTs.type({
  userId: ioTs.number,
  edit: EditCoder,
  productIds: ioTs.array(ioTs.number),
})

export type UserEdit = ioTs.TypeOf<typeof UserEditCoder>

export const UserEditInputCoder = ioTs.type({
  productIds: ioTs.array(ioTs.number),
})

export type UserEditInput = ioTs.TypeOf<typeof UserEditInputCoder>

export const UserEditPublicCoder = ioTs.type({
  ...editFields,
  coverImage: ioTs.union([ImageUrlCoder, ioTs.null]),
  products: ioTs.array(ProductWithImageCoder),
  user: UserWithImagesCoder,
})

export type UserEditPublic = ioTs.TypeOf<typeof UserEditPublicCoder>

export const UserEditFeedCoder = ioTs.type({
  user: UserProfileCoder,
  userEdits: ioTs.array(UserEditPublicCoder),
})

export const UserListFeedCoder = ioTs.type({
  user: UserProfileCoder,
  userLists: ioTs.array(UserListPublicCoder),
})

export const QueuedImageCoder = ioTs.type({
  id: ioTs.number,
  userId: ioTs.number,
  imageId: ioTs.number,
})

export type QueuedImage = ioTs.TypeOf<typeof QueuedImageCoder>

export const QueuedImageInputCoder = ioTs.type({
  imageId: ioTs.number,
})

export type QueuedImageInput = ioTs.TypeOf<typeof QueuedImageInputCoder>

const AnalyticSourceCoder = ioTs.union([
  ioTs.literal('web'),
  ioTs.literal('app'),
])

export type AnalyticSource = ioTs.TypeOf<typeof AnalyticSourceCoder>

export const MediaViewInputCoder = ioTs.type({
  mediaId: ioTs.number,
  source: AnalyticSourceCoder,
})

export type MediaViewInput = ioTs.TypeOf<typeof MediaViewInputCoder>

const ProductDiscoverySourceCoder = ioTs.union([
  ioTs.literal('feedFeature'),
  ioTs.literal('productRecommendation'),
  ioTs.literal('moreFromBrand'),
])

export type ProductDiscoverySource = ioTs.TypeOf<
  typeof ProductDiscoverySourceCoder
>

export const isProductDiscoverySource = (
  item: string | undefined
): item is ProductDiscoverySource => ProductDiscoverySourceCoder.is(item)

export const ProductViewInputCoder = ioTs.type({
  productId: ioTs.number,
  mediaId: ioTs.union([ioTs.number, ioTs.undefined]),
  userListId: ioTs.union([ioTs.number, ioTs.undefined]),
  discoverySource: ioTs.union([ProductDiscoverySourceCoder, ioTs.undefined]),
  analyticSource: ioTs.union([AnalyticSourceCoder, ioTs.undefined]),
})

export type ProductViewInput = ioTs.TypeOf<typeof ProductViewInputCoder>

export const OutgoingProductClickInputCoder = ProductViewInputCoder

export type OutgoingProductClickInput = ProductViewInput

export const OutgoingClickInputCoder = ioTs.type({
  creatorId: ioTs.number,
  url: ioTs.string,
  source: AnalyticSourceCoder,
})

export type OutgoingClickInput = ioTs.TypeOf<typeof OutgoingClickInputCoder>

export const BehindTheGateViewInputCoder = ioTs.type({
  creatorId: ioTs.number,
  source: AnalyticSourceCoder,
})

export type BehindTheGateViewInput = ioTs.TypeOf<
  typeof BehindTheGateViewInputCoder
>

export const EmailSourceCoder = ioTs.union([
  ioTs.literal('creatorWeb'),
  ioTs.literal('shopperWeb'),
])

export type EmailSource = ioTs.TypeOf<typeof EmailSourceCoder>

export const LoginInputCoder = ioTs.type({
  email: ioTs.string,
  password: ioTs.union([ioTs.string, ioTs.undefined]),
  resetPasswordToken: ioTs.union([ioTs.string, ioTs.undefined]),
})

export type LoginInput = ioTs.TypeOf<typeof LoginInputCoder>

export const VerifyEmailInputCoder = ioTs.type({
  email: ioTs.string,
  source: EmailSourceCoder,
})

export type VerifyEmailInput = ioTs.TypeOf<typeof VerifyEmailInputCoder>

export const LoginErrorCoder = ioTs.type({
  error: ioTs.union([
    ioTs.literal('incorrectEmailOrPassword'),
    ioTs.literal('notVerified'),
    ioTs.literal('invalidToken'),
    ioTs.literal('other'),
  ]),
})

const LoginSuccessCoder = ioTs.type({
  status: ioTs.literal('success'),
})

export type LoginError = ioTs.TypeOf<typeof LoginErrorCoder>

export const LoginCoder = ioTs.union([LoginErrorCoder, LoginSuccessCoder])

export type Login = ioTs.TypeOf<typeof LoginCoder>

export const isLoginError = (login: Login): login is LoginError =>
  'error' in login

export const VerificationStatusInputCoder = ioTs.type({
  email: ioTs.string,
})

export type VerificationStatusInput = ioTs.TypeOf<
  typeof VerificationStatusInputCoder
>

export const VerificationStatusCoder = ioTs.type({
  status: ioTs.union([
    ioTs.literal('verified'),
    ioTs.literal('pendingVerification'),
    ioTs.literal('notFound'),
  ]),
})
export const UserListViewInputCoder = ioTs.type({
  userListId: ioTs.number,
  source: AnalyticSourceCoder,
})

export type UserListViewInput = ioTs.TypeOf<typeof UserListViewInputCoder>

export const EditFeedViewInputCoder = ioTs.type({
  editId: ioTs.number,
  source: AnalyticSourceCoder,
})

export type EditFeedViewInput = ioTs.TypeOf<typeof EditFeedViewInputCoder>

const SearchResultClickCreatorCoder = ioTs.type({
  type: ioTs.literal('creator'),
  value: ioTs.number,
})

const SearchResultClickBrandCoder = ioTs.type({
  type: ioTs.literal('brand'),
  value: ioTs.number,
})

const SearchResultClickLabelCoder = ioTs.type({
  type: ioTs.literal('label'),
  value: ioTs.number,
})

export const SearchResultClickCoder = ioTs.union([
  SearchResultClickCreatorCoder,
  SearchResultClickBrandCoder,
  SearchResultClickLabelCoder,
])

export type SearchResultClick = ioTs.TypeOf<typeof SearchResultClickCoder>

export const SearchResultClickInputCoder = ioTs.type({
  result: SearchResultClickCoder,
})

export type SearchResultClickInput = ioTs.TypeOf<
  typeof SearchResultClickInputCoder
>

const InsightsStatsCoder = ioTs.type({
  mediaViews: ioTs.number,
  behindTheGateViews: ioTs.number,
  totalFollowers: ioTs.number,
  totalMedia: ioTs.number,
  totalEdits: ioTs.number,
})

const InsightsTopMediaCoder = ioTs.type({
  id: ioTs.number,
  content: FeedContentCoder,
  numViews: ioTs.number,
  createdAt: ISO8601DateTime,
  updatedAt: ISO8601DateTime,
})

export const InsightsCoder = ioTs.type({
  stats: InsightsStatsCoder,
  topMedia: ioTs.array(InsightsTopMediaCoder),
  topProducts: ioTs.array(ProductWithImageAndRelatedProductIdsCoder),
})

export type Insights = ioTs.TypeOf<typeof InsightsCoder>

export const CreatorEarningsCoder = ioTs.type({
  id: ioTs.number,
  isCommitted: ioTs.boolean,
  userId: ioTs.number,
  username: ioTs.union([ioTs.string, ioTs.null]),
  mediaViews: ioTs.number,
  productViews: ioTs.number,
  outgoingProductClicks: ioTs.number,
  mediaPublished: ioTs.number,
  mediaTagged: ioTs.number,
  editsCreated: ioTs.number,
  mediaViewEarnings: ioTs.number,
  productViewEarnings: ioTs.number,
  outgoingProductClickEarnings: ioTs.number,
  mediaPublishedEarnings: ioTs.number,
  mediaTaggedEarnings: ioTs.number,
  editsCreatedEarnings: ioTs.number,
  bonusEarnings: ioTs.union([ioTs.number, ioTs.undefined, ioTs.null]),
  partnershipEarnings: ioTs.union([ioTs.number, ioTs.undefined, ioTs.null]),
  updatedMediaViewEarnings: ioTs.union([
    ioTs.number,
    ioTs.undefined,
    ioTs.null,
  ]),
  updatedProductViewEarnings: ioTs.union([
    ioTs.number,
    ioTs.undefined,
    ioTs.null,
  ]),
  updatedOutgoingProductClickEarnings: ioTs.union([
    ioTs.number,
    ioTs.undefined,
    ioTs.null,
  ]),
  updatedMediaPublishedEarnings: ioTs.union([
    ioTs.number,
    ioTs.undefined,
    ioTs.null,
  ]),
  updatedMediaTaggedEarnings: ioTs.union([
    ioTs.number,
    ioTs.undefined,
    ioTs.null,
  ]),
  updatedEditsCreatedEarnings: ioTs.union([
    ioTs.number,
    ioTs.undefined,
    ioTs.null,
  ]),
  createdAt: ISO8601DateTime,
  updatedAt: ISO8601DateTime,
})

export type CreatorEarnings = ioTs.TypeOf<typeof CreatorEarningsCoder>

export const CreatorFundCostsCoder = ioTs.type({
  costPerMediaView: ioTs.number,
  costPerProductView: ioTs.number,
  costPerOutgoingProductClick: ioTs.number,
  costPerMediaPublished: ioTs.number,
  costPerMediaTagged: ioTs.number,
  costPerEditCreated: ioTs.number,
})

export type CreatorFundCosts = ioTs.TypeOf<typeof CreatorFundCostsCoder>

export const CreatorFundCostsInputCoder = ioTs.type({
  costPerMediaView: ioTs.number,
  costPerProductView: ioTs.number,
  costPerOutgoingProductClick: ioTs.number,
  costPerMediaPublished: ioTs.number,
  costPerMediaTagged: ioTs.number,
  costPerEditCreated: ioTs.number,
})

export type CreatorFundCostsInput = ioTs.TypeOf<
  typeof CreatorFundCostsInputCoder
>

export const CreatorEarningsOverviewCoder = ioTs.type({
  isCommitted: ioTs.boolean,
  date: ioTs.string,
  costs: CreatorFundCostsCoder,
  mediaViews: ioTs.number,
  productViews: ioTs.number,
  outgoingProductClicks: ioTs.number,
  mediaPublished: ioTs.number,
  mediaTagged: ioTs.number,
  editsCreated: ioTs.number,
  mediaViewEarnings: ioTs.number,
  productViewEarnings: ioTs.number,
  outgoingProductClickEarnings: ioTs.number,
  mediaPublishedEarnings: ioTs.number,
  mediaTaggedEarnings: ioTs.number,
  editsCreatedEarnings: ioTs.number,
  bonusEarnings: ioTs.number,
  partnershipEarnings: ioTs.number,
  createdAt: ISO8601DateTime,
  updatedAt: ISO8601DateTime,
})

export type CreatorEarningsOverview = ioTs.TypeOf<
  typeof CreatorEarningsOverviewCoder
>

const RedemptionRequestStatusCoder = ioTs.union([
  ioTs.literal('unprocessed'),
  ioTs.literal('processing'),
  ioTs.literal('fulfilled'),
  ioTs.literal('error'),
  ioTs.literal('deleted'),
])

export type RedemptionRequestStatus = ioTs.TypeOf<
  typeof RedemptionRequestStatusCoder
>

export const RedemptionTransactionTypeCoder = ioTs.type({
  type: ioTs.literal('redemption'),
  value: RedemptionRequestStatusCoder,
})

export type RedemptionRequestTransactionType = ioTs.TypeOf<
  typeof RedemptionTransactionTypeCoder
>

export const DailyEarningTransactionTypeCoder = ioTs.type({
  type: ioTs.literal('dailyEarning'),
})

export type DailyEarningTransactionType = ioTs.TypeOf<
  typeof DailyEarningTransactionTypeCoder
>

export const BonusTransactionTypeCoder = ioTs.type({
  type: ioTs.literal('bonus'),
})

export type BonusTransactionType = ioTs.TypeOf<typeof BonusTransactionTypeCoder>

export const PartnershipTransactionTypeCoder = ioTs.type({
  type: ioTs.literal('partnership'),
})

export type PartnershipTransactionType = ioTs.TypeOf<
  typeof PartnershipTransactionTypeCoder
>

const TransactionTypeCoder = ioTs.union([
  DailyEarningTransactionTypeCoder,
  BonusTransactionTypeCoder,
  PartnershipTransactionTypeCoder,
  RedemptionTransactionTypeCoder,
])

export type TransactionType = ioTs.TypeOf<typeof TransactionTypeCoder>

export const isRedemptionRequestTransactionType = (
  transactionType: TransactionType
): transactionType is RedemptionRequestTransactionType =>
  transactionType.type === 'redemption'

export const isDailyEarningTransactionType = (
  transactionType: TransactionType
): transactionType is DailyEarningTransactionType =>
  transactionType.type === 'dailyEarning'

export const isBonusTransactionType = (
  transactionType: TransactionType
): transactionType is BonusTransactionType => transactionType.type === 'bonus'

export const isPartnershipEarningTransactionType = (
  transactionType: TransactionType
): transactionType is PartnershipTransactionType =>
  transactionType.type === 'partnership'

export const CreatorMonetizationTransactionCoder = ioTs.type({
  date: ioTs.string,
  amount: ioTs.number,
  creatorEarningOrRedemptionRequestId: ioTs.number,
  transactionType: TransactionTypeCoder,
})

export type CreatorEarningsOverviewByDay = ioTs.TypeOf<
  typeof CreatorEarningsCreatorOverviewCoder
>

export const CreatorEarningsCreatorOverviewCoder = ioTs.type({
  totalEarnings: ioTs.number,
  currentBalance: ioTs.number,
  availableBalance: ioTs.number,
  transactions: ioTs.array(CreatorMonetizationTransactionCoder),
})

export type CreatorEarningsCreatorOverview = ioTs.TypeOf<
  typeof CreatorEarningsCreatorOverviewCoder
>

export const CreatorEarningsInputCoder = ioTs.type({
  id: ioTs.number,
  updatedMediaViewEarnings: ioTs.union([
    ioTs.number,
    ioTs.undefined,
    ioTs.null,
  ]),
  updatedProductViewEarnings: ioTs.union([
    ioTs.number,
    ioTs.undefined,
    ioTs.null,
  ]),
  updatedOutgoingProductClickEarnings: ioTs.union([
    ioTs.number,
    ioTs.undefined,
    ioTs.null,
  ]),
  bonusEarnings: ioTs.union([ioTs.number, ioTs.undefined, ioTs.null]),
  partnershipEarnings: ioTs.union([ioTs.number, ioTs.undefined, ioTs.null]),
})

export type CreatorEarningsInput = ioTs.TypeOf<typeof CreatorEarningsInputCoder>

export const CreatorEarningsManyInputCoder = ioTs.array(
  CreatorEarningsInputCoder
)

export type CreatorEarningsManyInput = ioTs.TypeOf<
  typeof CreatorEarningsManyInputCoder
>

export const CreatorEarningsDateInputCoder = ioTs.type({
  date: ioTs.string,
})

export type CreatorEarningsDateInput = ioTs.TypeOf<
  typeof CreatorEarningsDateInputCoder
>

export const IsAffiliatedProductInputCoder = ioTs.type({
  productUrl: ioTs.string,
})

export type IsAffiliatedProductInput = ioTs.TypeOf<
  typeof IsAffiliatedProductInputCoder
>

export const IsAffiliatedProductCoder = ioTs.type({
  isAffiliated: ioTs.boolean,
})

export type IsAffiliatedProduct = ioTs.TypeOf<typeof IsAffiliatedProductCoder>

export const RedemptionRequestInputCoder = ioTs.type({
  userId: ioTs.number,
  url: ioTs.string,
  inputPrice: ioTs.number,
  actualPrice: ioTs.union([ioTs.number, ioTs.undefined, ioTs.null]),
  size: ioTs.union([ioTs.string, ioTs.undefined, ioTs.null]),
  variant: ioTs.union([ioTs.string, ioTs.undefined, ioTs.null]),
  email: ioTs.string,
  shippingAddress: ioTs.string,
  notes: ioTs.union([ioTs.string, ioTs.undefined, ioTs.null]),
  status: ioTs.union([RedemptionRequestStatusCoder, ioTs.undefined]),
})

export type RedemptionRequestInput = ioTs.TypeOf<
  typeof RedemptionRequestInputCoder
>

const redemptionRequestFields = {
  id: ioTs.number,
  userId: ioTs.number,
  url: ioTs.string,
  inputPrice: ioTs.number,
  actualPrice: ioTs.number,
  size: ioTs.union([ioTs.string, ioTs.undefined, ioTs.null]),
  variant: ioTs.union([ioTs.string, ioTs.undefined, ioTs.null]),
  email: ioTs.string,
  shippingAddress: ioTs.string,
  notes: ioTs.union([ioTs.string, ioTs.undefined, ioTs.null]),
  status: RedemptionRequestStatusCoder,
  createdAt: ISO8601DateTime,
  updatedAt: ISO8601DateTime,
}
export const RedemptionRequestCoder = ioTs.type({
  ...redemptionRequestFields,
})

export type RedemptionRequest = ioTs.TypeOf<typeof RedemptionRequestCoder>

export const BrandProductStatsCoder = ioTs.type({
  id: ioTs.number,
  name: ioTs.string,
  matches: ioTs.array(ioTs.number),
  saves: ioTs.number,
  views: ioTs.number,
  outgoingClicks: ioTs.number,
})

export type BrandProductStats = ioTs.TypeOf<typeof BrandProductStatsCoder>

export const RadarsCoder = ioTs.type({
  id: ioTs.number,
  title: ioTs.union([ioTs.string, ioTs.undefined, ioTs.null]),
  intro: ioTs.union([ioTs.string, ioTs.undefined, ioTs.null]),
  url: ioTs.union([ioTs.string, ioTs.undefined, ioTs.null]),
  smallImage: ioTs.union([ioTs.string, ioTs.undefined, ioTs.null]),
  image: ioTs.union([ioTs.string, ioTs.undefined, ioTs.null]),
  publishedAt: ioTs.union([ISO8601DateTime, ioTs.undefined, ioTs.null]),
  createdAt: ISO8601DateTime,
  updatedAt: ISO8601DateTime,
})

export const RadarsFeedCoder = ioTs.array(RadarsCoder)

export type Radars = ioTs.TypeOf<typeof RadarsCoder>

export const PaginatedRadarsCoder = ioTs.type({
  pages: ioTs.union([ioTs.number, ioTs.null]),
  pagination_seed: ioTs.number,
  data: RadarsFeedCoder,
})

export const BannerResponseCoder = ioTs.type({
  fullImage: ioTs.union([ioTs.string, ioTs.null]),
  content: ioTs.union([ioTs.object, ioTs.null]),
})

export type BannerResponse = ioTs.TypeOf<typeof BannerResponseCoder>
